import { useEffect, useRef, useState } from "react";
import { api, type JourneyPerson, type JourneySearchHit, type JourneyTouch } from "../lib/api.ts";
import { money } from "../lib/format.ts";
import { Card } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { currentLang, dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { CHANNEL, UI, WRITEBACK_SKIP } from "../i18n/dict.ts";

/**
 * Client lookup — справочник пути конкретного человека.
 *
 * Зачем отдельно от Customer Journey. Агрегат нельзя проверить: «308 циклов
 * пришли из рекламы» либо принимают на веру, либо не принимают вовсе. А путь
 * одного человека сверяется с карточкой в Битриксе и с памятью менеджера за
 * минуту. Доверие к отчёту растёт снизу — от проверяемых частностей.
 *
 * Поэтому здесь показывается не только «что», но и «почему»: у каждого решения
 * об источнике видно ссылку из первого сообщения, во что она развернулась,
 * сколько объявлений подошло под ключ и подтверждён ли показ. Такое решение
 * можно оспорить — и это его главное достоинство.
 */

/** Значок касания: тип → символ. Порядок и смысл те же, что в воронке. */
const TOUCH_MARK: Record<JourneyTouch["touch_kind"], string> = {
  prompt: "◇",
  inquiry: "●",
  reached_manager: "◆",
  deal_created: "■",
  paid: "▲",
  won: "★",
};

/**
 * Подпись касания по его типу.
 *
 * Тип (`touch_kind`) приходит с сервера ключом, английская подпись — рядом с
 * ним, и она остаётся запасным вариантом: тип касания, которого ещё нет в
 * словаре, покажет подпись из ответа, а не пустоту. Ключи касаний НЕ совпадают
 * с ключами ступеней воронки (`JOURNEY_STEP`): там пять ступеней когорты, здесь
 * шесть типов касания, включая «увидел рекламу», которого в воронке нет.
 */
const TOUCH_NAME = {
  prompt: { ru: "Увидел рекламу", en: "Saw the advertising" },
  inquiry: { ru: "Обращение", en: "Inquiry" },
  reached_manager: { ru: "Стал квалифицированным лидом", en: "Became a qualified lead" },
  deal_created: { ru: "Создана сделка", en: "Deal created" },
  paid: { ru: "Деньги получены", en: "Money received" },
  won: { ru: "Банкет прошёл", en: "Banquet happened" },
} satisfies Record<JourneyTouch["touch_kind"], L>;

/**
 * Буква семантики — внутренний код Битрикса. В интерфейсе её быть не должно:
 * «P» ничего не сообщает даже тому, кто в портале работает каждый день.
 */
const SEMANTIC = {
  P: { ru: "в работе", en: "in progress" },
  S: { ru: "закрыт — успех", en: "closed — won" },
  F: { ru: "закрыт — провал", en: "closed — lost" },
} satisfies Record<string, L>;

const CONFIDENCE_NOTE = {
  unique: { ru: "ссылке подошло одно объявление — однозначно", en: "one ad matched the link — unambiguous" },
  delivered: {
    ru: "ссылку делили несколько объявлений; взяли то, что показывалось в тот день",
    en: "several ads shared the link; the one running that day was taken",
  },
  ambiguous: {
    ru: "несколько объявлений, ни одно не доказать — оставлено без источника",
    en: "several ads, none provable — left unattributed",
  },
  conflict: {
    ru: "объявление принадлежит другому бренду — не использовано",
    en: "the ad belongs to another brand — not used",
  },
  organic: { ru: "пост есть, но рекламы за ним нет", en: "the post exists but no ad is behind it" },
  unresolved: {
    ru: "ссылка была, но развернуть её не удалось",
    en: "a link was there but could not be resolved",
  },
  nolink: {
    ru: "в первом сообщении ссылки нет — разворачивать нечего",
    en: "no link in the first message — nothing to resolve",
  },
  deferred: { ru: "бюджет прогона кончился; попробуем снова", en: "budget ran out this run; will be retried" },
  outside: { ru: "вне окна данных о показах", en: "outside the delivery-data window" },
  starter: {
    ru: "ссылки нет, но первое сообщение — стартовый текст кнопки WhatsApp из объявления; платформа известна, кампания — нет",
    en: "no link, but the first message is the WhatsApp button's starter text from an ad; the platform is known, the campaign is not",
  },
  site_ref: {
    ru: "первое сообщение отправлено кнопкой WhatsApp на сайте бренда; источник, канал и кампанию сайт снял с перехода посетителя",
    en: "the first message came from the WhatsApp button on a brand website; the site took the source, medium and campaign from the visitor's arrival",
  },
} satisfies Record<string, L>;

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
/**
 * Причина, по которой метку не записали в Битрикс.
 *
 * Ключ и подробности разделены двоеточием (`busy:UF_...=значение`): переводится
 * часть до двоеточия, остаток печатается как есть — по нему человек и решает,
 * наша это метка или чужая работа. У записей до 10.09.2026 в журнале лежит
 * русская проза вместо ключа, и она покажется запасным вариантом.
 */
function writebackSkip(raw: string): string {
  const idx = raw.indexOf(":");
  const key = idx === -1 ? raw : raw.slice(0, idx);
  const rest = idx === -1 ? "" : raw.slice(idx + 1).trim();
  const phrase = dbLabel(WRITEBACK_SKIP, key, raw);
  return rest === "" ? phrase : `${phrase}: ${rest}`;
}

const TX = {
  /* --- поиск */
  findClient: { ru: "Найти клиента", en: "Find a client" },
  findClientHint: {
    ru: "Поиск по имени, ПОСЛЕДНИМ цифрам номера телефона, id лида или id сделки. Фильтры периода здесь не действуют.",
    en: "Search by name, the LAST digits of a phone number, a lead id or a deal id. Period filters do not apply here.",
  },
  searchPlaceholder: {
    ru: "имя · последние цифры телефона · id лида",
    en: "name · last digits of phone · lead id",
  },
  searchClientsAria: { ru: "Поиск клиентов", en: "Search clients" },
  typeAtLeast3: { ru: "Введите хотя бы 3 символа.", en: "Type at least 3 characters." },
  searching: { ru: "поиск…", en: "searching…" },
  noName: { ru: "(без имени)", en: "(no name)" },
  /* Счётные существительные: в русском три формы. */
  cyclesWord: {
    one: { ru: "цикл", en: "cycle" },
    few: { ru: "цикла", en: "cycles" },
    many: { ru: "циклов", en: "cycles" },
  },
  leadsWord: {
    one: { ru: "лид", en: "lead" },
    few: { ru: "лида", en: "leads" },
    many: { ru: "лидов", en: "leads" },
  },
  banquetsWord: {
    one: { ru: "банкет", en: "banquet" },
    few: { ru: "банкета", en: "banquets" },
    many: { ru: "банкетов", en: "banquets" },
  },

  /* --- путь человека */
  customerPath: { ru: "Путь клиента", en: "Customer path" },
  pickClient: {
    ru: "Выберите клиента слева, чтобы увидеть каждое касание, каждый лид и каждую сделку — а там, где источник восстановлен по переписке, ещё и основание этого решения.",
    en: "Pick a client on the left to see every touch, every lead and every deal — and, where the source was reconstructed from the chat, the evidence behind that decision.",
  },
  loadingPerson: { ru: "загрузка…", en: "loading…" },
  /* Оговорка про личные данные: лид без телефона не склеивается ни с кем, и
     человек должен это прочитать до того, как решит, что история полная. */
  noPhoneOnLead: {
    ru: "У этого лида нет номера телефона, поэтому его нельзя соединить с другими обращениями того же человека. Если он писал раньше из другого канала, та история покажется отдельным клиентом.",
    en: "This lead carries no phone number, so it cannot be joined with the same person’s other inquiries. If they wrote before from another channel, that history shows up as a different client.",
  },
  cycles: { ru: "Циклы", en: "Cycles" },
  touches: { ru: "Касания", en: "Touches" },

  /* --- лента касаний */
  timeline: { ru: "Лента касаний", en: "Timeline" },
  timelineHint: {
    ru: "Все касания по порядку. После состоявшегося банкета цикл начинается заново.",
    en: "Every touch in order. A cycle restarts after a completed banquet.",
  },
  leadRef: { ru: "лид", en: "lead" },
  dealRef: { ru: "сделка", en: "deal" },

  /* --- циклы */
  cyclesHint: {
    ru: "По строке на цикл: чем привели и докуда дошёл.",
    en: "One row per cycle: what brought them in, how far they got.",
  },
  colStarted: { ru: "Начался", en: "Started" },
  colBroughtInBy: { ru: "Чем привели", en: "Brought in by" },
  colConvertedThrough: { ru: "Через что сконвертировался", en: "Converted through" },
  colReached: { ru: "Докуда дошёл", en: "Reached" },
  repeatCycleTitle: { ru: "вернулся после завершённого цикла", en: "came back after a completed cycle" },
  /* Два ключа на одну английскую формулировку: по-русски у кампании и у
     источника разный род — «не определена» и «не определён». */
  notIdentifiedCampaign: { ru: "не определена", en: "not identified" },
  notIdentifiedSource: { ru: "не определён", en: "not identified" },
  reachedWon: { ru: "банкет прошёл", en: "banquet happened" },
  reachedPaid: { ru: "деньги получены", en: "money received" },
  reachedDeal: { ru: "создана сделка", en: "deal created" },
  reachedManager: { ru: "квалифицированный лид", en: "qualified lead" },
  reachedEnquiryOnly: { ru: "только обращение", en: "inquiry only" },

  /* --- лиды и сделки */
  leadsHint: { ru: "Как они выглядят в Битриксе прямо сейчас.", en: "As they stand in Bitrix right now." },
  colCreated: { ru: "Создан", en: "Created" },
  colChannel: { ru: "Канал", en: "Channel" },
  colTagInBitrix: { ru: "Метка в Битриксе", en: "Tag in Bitrix" },
  botHandled: { ru: "это обращение вёл бот", en: "the bot handled this inquiry" },
  botMark: { ru: "бот", en: "bot" },
  none: { ru: "нет", en: "none" },
  colEventDate: { ru: "Дата праздника", en: "Event date" },
  colAmount: { ru: "Сумма", en: "Amount" },
  colPaid: { ru: "Оплачено", en: "Paid" },

  /* --- основание решения об источнике */
  adRef: { ru: "объявление", en: "ad" },
  starterRef: { ru: "кнопка WhatsApp в объявлении, ассет", en: "WhatsApp button in an ad, asset" },
  siteRef: { ru: "кнопка WhatsApp на сайте бренда:", en: "WhatsApp button on a brand website:" },
  linkInFirstMessage: { ru: "ссылка из первого сообщения:", en: "link in the first message:" },
  keyWord: { ru: "ключ", en: "key" },
  adsMatchedKey: {
    one: { ru: "объявление подошло под этот ключ", en: "ad matched that key" },
    few: { ru: "объявления подошли под этот ключ", en: "ads matched that key" },
    many: { ru: "объявлений подошло под этот ключ", en: "ads matched that key" },
  },
  deliveryConfirmed: {
    ru: "показ в день сообщения подтверждён",
    en: "delivery confirmed on the day of the message",
  },
  deliveryNotConfirmed: { ru: "показ не подтверждён", en: "delivery not confirmed" },
  writtenBackOn: { ru: "записано в Битрикс", en: "written back to Bitrix on" },
  asWord: { ru: "как", en: "as" },
  /* Чья метка — часть основания: см. правило про tag_origin. */
  tagByAnalytics: {
    ru: "метку поставила аналитика, её не принёс переход",
    en: "the tag was put there by analytics, not carried by the click",
  },
  notWrittenBack: { ru: "в Битрикс не записано:", en: "not written back:" },
  titleMeasured: { ru: "переход принёс метку — измерено", en: "the click carried the tag — measured" },
  titleInferred: {
    ru: "восстановлено по ссылке из переписки — выведено",
    en: "reconstructed from the link in the chat — inferred",
  },
  badgeMeasured: { ru: "измерено", en: "measured" },
  badgeInferred: { ru: "выведено", en: "inferred" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

export function JourneyExplorer(): React.ReactElement {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<JourneySearchHit[]>([]);
  const [hint, setHint] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [person, setPerson] = useState<JourneyPerson | null>(null);
  const [loadingPerson, setLoadingPerson] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Поиск с задержкой в 300 мс.
   *
   * Без неё каждая нажатая буква — запрос с `ILIKE '%…%'` по восьмидесяти
   * тысячам лидов. Задержка здесь не украшение интерфейса, а то, что не даёт
   * набору имени превратиться в восемь полных сканов.
   */
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    if (q.trim().length < 3) {
      setHits([]);
      setHint(q.trim() ? t(TX.typeAtLeast3) : null);
      return;
    }
    setSearching(true);
    // Таймер назван `timer`, а не `t`: `t` — это переводчик подписей, и внутри
    // этого блока он нужен строкой выше.
    const timer = setTimeout(() => {
      api
        .journeySearch(q)
        .then((r) => {
          // Ответ на устаревший запрос игнорируем: иначе медленный ответ на
          // «An» перезапишет быстрый на «Anna».
          if (mine !== seq.current) return;
          setHits(r.hits);
          setHint(r.hint);
        })
        .catch((e: unknown) => setError(String(e)))
        .finally(() => {
          if (mine === seq.current) setSearching(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    if (!selected) {
      setPerson(null);
      return;
    }
    setLoadingPerson(true);
    api
      .journeyPerson(selected)
      .then(setPerson)
      .catch((e: unknown) => setError(String(e)))
      .finally(() => setLoadingPerson(false));
  }, [selected]);

  return (
    <div className="grid" style={{ gridTemplateColumns: "minmax(300px, 380px) 1fr", gap: 14 }}>
      <Card title={t(TX.findClient)} hint={t(TX.findClientHint)}>
        {/*
          Подсказка описывает ФОРМУ ввода, а не притворяется данными. Прежний
          пример «Anna · +971 50 · 199139» звал набрать начало номера, а поиск
          сравнивает ХВОСТ: «+971 50» находило один посторонний лид
          (+971588597150), а строка целиком — ничего. Форма не устаревает вместе
          с базой и не обещает того, чего поиск не делает.
        */}
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t(TX.searchPlaceholder)}
          aria-label={t(TX.searchClientsAria)}
          style={{
            width: "100%",
            padding: "8px 10px",
            fontSize: 14,
            marginBottom: 10,
            boxSizing: "border-box",
          }}
        />
        {searching && <div className="muted">{t(TX.searching)}</div>}
        {hint && !searching && <div className="muted">{hint}</div>}
        {error && <div style={{ color: "var(--bad)" }}>{error}</div>}

        {/*
          НАЙДЕННЫЕ ЛЮДИ СОРТИРОВКИ НЕ ПОЛУЧАЮТ, и причин две.
          Первая: это не таблица, а список кнопок — строки заголовков, к которой
          сортировку цепляют, здесь нет, а колонок нет и у самих строк (имя,
          телефон и счётчики стоят в одной кнопке).
          Вторая, и она весомее: сервер отдаёт не всю выборку, а первые 25
          (`api/journeyExplorer.ts:141`, `LIMIT 25 + 1`), и когда людей больше,
          он так и говорит подсказкой «Narrow the query». Отсортировать эти 25 по
          выручке значило бы показать «самого дорогого клиента» из случайной
          четверти совпадений. Способ добраться до нужного человека здесь —
          уточнить запрос, а не переставить срез.
        */}
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {hits.map((h) => (
            <li key={h.person_key}>
              <button
                type="button"
                onClick={() => setSelected(h.person_key)}
                aria-current={selected === h.person_key ? "true" : undefined}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "7px 8px",
                  border: "none",
                  borderBottom: "1px solid var(--line)",
                  background:
                    selected === h.person_key ? "var(--accent-soft)" : "none",
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                }}
              >
                <div style={{ fontWeight: 600 }}>{h.display_name ?? t(TX.noName)}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {h.phone_e164 ?? h.person_key} · {h.cycles} {plural(h.cycles, TX.cyclesWord)} ·{" "}
                  {h.leads} {plural(h.leads, TX.leadsWord)}
                  {h.won_cycles > 0 && ` · ${h.won_cycles} ${plural(h.won_cycles, TX.banquetsWord)}`}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <div>
        {!selected && (
          <Card title={t(TX.customerPath)}>
            <p className="muted" style={{ margin: 0 }}>
              {t(TX.pickClient)}
            </p>
          </Card>
        )}
        {selected && loadingPerson && (
          <Card title={t(TX.customerPath)}>
            <div className="muted">{t(TX.loadingPerson)}</div>
          </Card>
        )}
        {selected && !loadingPerson && person && <PersonPath data={person} />}
      </div>
    </div>
  );
}

function PersonPath({ data }: { data: JourneyPerson }): React.ReactElement {
  const p = data.person;
  const byLead = new Map(data.attribution.map((a) => [a.lead_id, a]));
  /* Слова печатаются рядом с номером лида и сделки. Взяты до обхода касаний:
     внутри него `t` — это касание, а не переводчик подписей. */
  const leadWord = t(TX.leadRef);
  const dealWord = t(TX.dealRef);

  /*
   * Все три таблицы человека приходят целиком: запросы пути (`cycles`, `leads`,
   * `deals` в `api/journeyExplorer.ts`) идут без `LIMIT`, потому что разрез — один
   * человек. Значит сортировать их можно честно: верхняя строка после клика —
   * действительно максимум по всему, что у человека есть.
   */
  const cyc = useSortable(data.cycles, {
    no: { kind: "number", get: (c) => c.cycle_no },
    /* Дата сравнивается СЫРОЙ строкой из ответа, а не тем, что печатает
       `when()`: там месяц стоит словом («10 сен 26»), и алфавит поставил бы
       август после апреля. ISO-строки коллатор с `numeric` упорядочивает верно. */
    started: { kind: "text", get: (c) => c.started_at },
    first: { kind: "text", get: (c) => c.first_channel },
    last: { kind: "text", get: (c) => c.last_channel },
    campaign: { kind: "text", get: (c) => c.first_campaign },
    /* «Докуда дошёл» — вычисляемая колонка: ячейка складывает фразу из четырёх
       признаков. Сравнивать её ПЕЧАТНЫЙ вид нечем — алфавит фраз «банкет прошёл»
       и «только обращение» не порядок, и он ещё и разный на двух языках. Порядок
       здесь один осмысленный — глубина воронки, и `reachedRank` повторяет ТУ ЖЕ
       цепочку проверок, что `reached()`. */
    reached: { kind: "number", get: (c) => reachedRank(c) },
    revenue: { kind: "number", get: (c) => c.revenue_aed_minor },
  });

  const lead = useSortable(data.leads, {
    id: { kind: "number", get: (l) => l.lead_id },
    created: { kind: "text", get: (l) => l.date_create },
    /* Пустая строка вместо запасного прочерка: у лида без имени стадии ячейка
       печатает «—», и такая строка обязана уйти в конец, а не встать по алфавиту
       между настоящими стадиями. */
    stage: { kind: "text", get: (l) => l.stage_name ?? dbLabel(SEMANTIC, l.status_semantic, "") },
    park: { kind: "text", get: (l) => l.park_key },
    channel: {
      kind: "text",
      get: (l) => (l.channel_name === null ? null : dbLabel(CHANNEL, l.channel_key, l.channel_name)),
    },
    manager: { kind: "text", get: (l) => l.assigned_name },
    tag: { kind: "text", get: (l) => l.utm_source },
  });

  const deal = useSortable(data.deals, {
    id: { kind: "number", get: (d) => d.deal_id },
    created: { kind: "text", get: (d) => d.date_create },
    event: { kind: "text", get: (d) => d.date_of_event },
    stage: { kind: "text", get: (d) => d.stage_name },
    manager: { kind: "text", get: (d) => d.assigned_name },
    amount: { kind: "number", get: (d) => d.opportunity_aed_minor },
    deposit: { kind: "number", get: (d) => d.deposit_aed_minor },
    paid: { kind: "text", get: (d) => d.paid_at },
  });

  return (
    <>
      <Card
        title={p?.display_name ?? t(TX.noName)}
        hint={[p?.phone_e164, p?.email].filter(Boolean).join(" · ") || undefined}
      >
        {p?.identified_by_lead_only && (
          <p className="muted" style={{ marginTop: 0 }}>
            {t(TX.noPhoneOnLead)}
          </p>
        )}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <Fact label={t(TX.cycles)} value={String(data.cycles.length)} />
          <Fact label={t(TX.touches)} value={String(data.touches.length)} />
          <Fact label={t(UI.leads)} value={String(data.leads.length)} />
          <Fact label={t(UI.deals)} value={String(data.deals.length)} />
          <Fact
            label={t(UI.revenue)}
            value={money(
              data.cycles.reduce((n, c) => n + Number(c.revenue_aed_minor), 0),
              "AED",
            )}
          />
        </div>
      </Card>

      {/*
        ЛЕНТА КАСАНИЙ ОСТАЁТСЯ ХРОНОЛОГИЕЙ И СОРТИРОВКИ НЕ ПОЛУЧАЕТ.
        Это не таблица: заголовков нет, а строка — не набор колонок, а один
        рассказ, внутрь которого вложено основание решения об источнике
        (`<Evidence>`). Цеплять сортировку не к чему, и цеплять её нечем: сервер
        отдаёт касания по `ORDER BY t.touch_at, t.touch_order`
        (`api/journeyExplorer.ts:233`), то есть по времени, а внутри одной
        отметки времени — по порядку вида касания. Второй ключ на клиенте не
        воспроизвести: `touch_order` в ответе не приезжает. Переставить эту
        ленту любым другим ключом значило бы сломать то единственное, что она
        сообщает, — в каком порядке всё было.
      */}
      <Card title={t(TX.timeline)} hint={t(TX.timelineHint)}>
        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {data.touches.map((t, i) => {
            const ev = t.lead_id ? byLead.get(t.lead_id) : undefined;
            return (
              <li
                key={`${t.touch_at}-${t.touch_kind}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "20px 132px 1fr",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid var(--line)",
                  alignItems: "baseline",
                }}
              >
                <span aria-hidden="true" style={{ opacity: 0.7 }}>
                  {TOUCH_MARK[t.touch_kind]}
                </span>
                <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {when(t.touch_at)}
                </span>
                <span>
                  <strong>{dbLabel(TOUCH_NAME, t.touch_kind, t.touch_name)}</strong>
                  {t.channel_name !== null && <> · {dbLabel(CHANNEL, t.channel_key, t.channel_name)}</>}
                  {t.park_key && <> · {t.park_key}</>}
                  {Number(t.amount_aed_minor) > 0 && (
                    <> · {money(Number(t.amount_aed_minor), "AED")}</>
                  )}
                  {t.lead_id && (
                    <span className="muted" style={{ fontSize: 12 }}>
                      {" "}
                      {leadWord} {t.lead_id}
                      {t.deal_id && ` · ${dealWord} ${t.deal_id}`}
                    </span>
                  )}
                  {t.campaign_name && (
                    <div style={{ fontSize: 12 }}>
                      <TagBadge source={t.campaign_source} /> {t.campaign_name}
                    </div>
                  )}
                  {/**
                   * Основание решения показываем прямо под касанием обращения —
                   * там, где оно возникло. В отдельной таблице внизу его бы
                   * никто не связал с конкретным обращением.
                   */}
                  {t.touch_kind === "inquiry" && ev && <Evidence ev={ev} />}
                </span>
              </li>
            );
          })}
        </ol>
      </Card>

      {data.cycles.length > 0 && (
        <Card title={t(TX.cycles)} hint={t(TX.cyclesHint)}>
          <table className="data">
            <thead>
              <tr>
                <Th sort={cyc} col="no">#</Th>
                <Th sort={cyc} col="started">{t(TX.colStarted)}</Th>
                <Th sort={cyc} col="first">{t(TX.colBroughtInBy)}</Th>
                <Th sort={cyc} col="last">{t(TX.colConvertedThrough)}</Th>
                <Th sort={cyc} col="campaign">{t(UI.campaign)}</Th>
                <Th sort={cyc} col="reached">{t(TX.colReached)}</Th>
                <Th sort={cyc} col="revenue" num>{t(UI.revenue)}</Th>
              </tr>
            </thead>
            <tbody>
              {cyc.rows.map((c) => (
                <tr key={c.cycle_no}>
                  <td>
                    {Number(c.cycle_no) + 1}
                    {c.is_repeat_cycle && (
                      <span className="muted" title={t(TX.repeatCycleTitle)}>
                        {" "}
                        ↺
                      </span>
                    )}
                  </td>
                  <td className="muted">{when(c.started_at)}</td>
                  <td>{c.first_channel ?? "—"}</td>
                  <td>{c.last_channel ?? "—"}</td>
                  <td>
                    {c.first_campaign ? (
                      <>
                        <TagBadge source={c.first_campaign_source} /> {c.first_campaign}
                      </>
                    ) : (
                      <span className="muted">{t(TX.notIdentifiedCampaign)}</span>
                    )}
                  </td>
                  <td>{reached(c)}</td>
                  <td className="num">{money(Number(c.revenue_aed_minor), "AED")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {data.leads.length > 0 && (
        <Card title={t(UI.leads)} hint={t(TX.leadsHint)}>
          <table className="data">
            <thead>
              <tr>
                <Th sort={lead} col="id">{t(UI.lead)}</Th>
                <Th sort={lead} col="created">{t(TX.colCreated)}</Th>
                <Th sort={lead} col="stage">{t(UI.stage)}</Th>
                <Th sort={lead} col="park">{t(UI.park)}</Th>
                <Th sort={lead} col="channel">{t(TX.colChannel)}</Th>
                <Th sort={lead} col="manager">{t(UI.salesperson)}</Th>
                <Th sort={lead} col="tag">{t(TX.colTagInBitrix)}</Th>
              </tr>
            </thead>
            <tbody>
              {lead.rows.map((l) => (
                <tr key={l.lead_id}>
                  <td>{l.lead_id}</td>
                  <td className="muted">{when(l.date_create)}</td>
                  {/* Название стадии из Битрикса не переводится; переводится
                      только наша расшифровка буквы семантики. */}
                  <td>{l.stage_name ?? dbLabel(SEMANTIC, l.status_semantic, "—")}</td>
                  <td>{l.park_key ?? "—"}</td>
                  <td>
                    {l.channel_name === null ? "—" : dbLabel(CHANNEL, l.channel_key, l.channel_name)}
                    {l.bot_flag && (
                      <span className="muted" title={t(TX.botHandled)}>
                        {" "}
                        {t(TX.botMark)}
                      </span>
                    )}
                  </td>
                  <td>{l.assigned_name ?? "—"}</td>
                  <td>
                    {l.utm_source ? (
                      <>
                        {l.utm_source}
                        <span className="muted" style={{ fontSize: 12 }}>
                          {" "}
                          ({l.utm_field_set})
                        </span>
                      </>
                    ) : (
                      <span className="muted">{t(TX.none)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {data.deals.length > 0 && (
        <Card title={t(UI.deals)}>
          <table className="data">
            <thead>
              <tr>
                <Th sort={deal} col="id">{t(UI.deal)}</Th>
                <Th sort={deal} col="created">{t(TX.colCreated)}</Th>
                <Th sort={deal} col="event">{t(TX.colEventDate)}</Th>
                <Th sort={deal} col="stage">{t(UI.stage)}</Th>
                <Th sort={deal} col="manager">{t(UI.salesperson)}</Th>
                <Th sort={deal} col="amount" num>{t(TX.colAmount)}</Th>
                <Th sort={deal} col="deposit" num>{t(UI.deposit)}</Th>
                <Th sort={deal} col="paid">{t(TX.colPaid)}</Th>
              </tr>
            </thead>
            <tbody>
              {deal.rows.map((d) => (
                <tr key={d.deal_id}>
                  <td>{d.deal_id}</td>
                  <td className="muted">{when(d.date_create)}</td>
                  <td>{d.date_of_event ? when(d.date_of_event) : "—"}</td>
                  <td>{d.stage_name ?? "—"}</td>
                  <td>{d.assigned_name ?? "—"}</td>
                  <td className="num">
                    {d.opportunity_aed_minor ? money(Number(d.opportunity_aed_minor), "AED") : "—"}
                  </td>
                  <td className="num">
                    {d.deposit_aed_minor ? money(Number(d.deposit_aed_minor), "AED") : "—"}
                  </td>
                  <td className="muted">{d.paid_at ? when(d.paid_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/**
 * Основание решения об источнике.
 *
 * Это не отладочная информация, а суть услуги: без неё «кампания X» —
 * утверждение, которое нечем проверить. Ссылка, ключ, число подошедших
 * объявлений и подтверждение показа делают его выводом.
 */
function Evidence({ ev }: { ev: JourneyPerson["attribution"][number] }): React.ReactElement {
  const known = ev.ad_external_id !== null;
  /* Стартовый текст кнопки WhatsApp: платформа известна, объявления нет — печатается ассет кнопки. */
  const starter = ev.confidence === "starter" && ev.platform !== null;
  /* Код сайта бренда: печатается как пришёл — сайт, источник/канал/кампания. */
  const siteRef = ev.confidence === "site_ref" && ev.matched_key !== null;
  return (
    <div
      style={{
        marginTop: 4,
        padding: "6px 8px",
        fontSize: 12,
        borderLeft: "2px solid var(--line)",
        background: "var(--panel-2)",
      }}
    >
      <div>
        <strong>
          {t(UI.source)}:{" "}
          {known
            ? `${ev.platform} · ${t(TX.adRef)} ${ev.ad_external_id}`
            : starter
              ? `${ev.platform} · ${t(TX.starterRef)} ${ev.matched_key ?? "—"}`
              : siteRef
                ? `${t(TX.siteRef)} ${ev.matched_key}`
                : t(TX.notIdentifiedSource)}
        </strong>
        <span className="muted"> — {dbLabel(CONFIDENCE_NOTE, ev.confidence, ev.confidence)}</span>
      </div>
      {ev.link_url && (
        <div className="muted">
          {t(TX.linkInFirstMessage)} {ev.link_kind} · {ev.link_url}
          {ev.matched_key && (
            <>
              {" "}
              → {t(TX.keyWord)} {ev.matched_key}
            </>
          )}
        </div>
      )}
      {known && (
        <div className="muted">
          {ev.candidates} {plural(ev.candidates, TX.adsMatchedKey)}
          {ev.delivery_confirmed
            ? ` · ${t(TX.deliveryConfirmed)}`
            : ` · ${t(TX.deliveryNotConfirmed)}`}
        </div>
      )}
      {/**
       * Написали ли мы вывод в Битрикс — часть основания: иначе непонятно,
       * почему в карточке лида метка есть, хотя переход её не приносил.
       */}
      {ev.written_at && (
        <div className="muted">
          {t(TX.writtenBackOn)} {when(ev.written_at)} {t(TX.asWord)} {ev.written_utm_source} /
          cpc-inferred — {t(TX.tagByAnalytics)}
        </div>
      )}
      {ev.skipped_reason && (
        <div className="muted">
          {t(TX.notWrittenBack)} {writebackSkip(ev.skipped_reason)}
        </div>
      )}
    </div>
  );
}

/**
 * Метка измеренная или выведенная. Один значок, но именно он не даёт спутать
 * «клиент донёс метку» с «мы восстановили источник по переписке».
 */
function TagBadge({ source }: { source: "utm" | "inferred" | null }): React.ReactElement | null {
  if (!source) return null;
  const measured = source === "utm";
  return (
    <span
      title={measured ? t(TX.titleMeasured) : t(TX.titleInferred)}
      style={{
        fontSize: 11,
        padding: "1px 5px",
        borderRadius: 3,
        border: "1px solid var(--line)",
        opacity: measured ? 1 : 0.75,
      }}
    >
      {measured ? t(TX.badgeMeasured) : t(TX.badgeInferred)}
    </span>
  );
}

function Fact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function reached(c: JourneyPerson["cycles"][number]): string {
  if (c.won) return t(TX.reachedWon);
  if (c.paid) return t(TX.reachedPaid);
  if (c.became_deal) return t(TX.reachedDeal);
  if (c.reached_manager) return t(TX.reachedManager);
  return t(TX.reachedEnquiryOnly);
}

/**
 * Глубина воронки того же цикла — для сортировки колонки «Докуда дошёл».
 *
 * Порядок проверок ТОТ ЖЕ, что в `reached()`, и меняться они обязаны вместе:
 * это одна колонка, только один возвращает фразу, а другой её место в воронке.
 * Числом, а не текстом, потому что порядок здесь — не алфавит: «банкет прошёл»
 * дальше, чем «создана сделка», на любом языке.
 */
function reachedRank(c: JourneyPerson["cycles"][number]): number {
  if (c.won) return 5;
  if (c.paid) return 4;
  if (c.became_deal) return 3;
  if (c.reached_manager) return 2;
  return 1;
}

/**
 * Дата и время в Дубае: парки там, и любое «когда» читают в местном времени.
 *
 * Локаль идёт за языком интерфейса — тем же правилом, что и `shortDate()` в
 * `lib/format.ts`: en-GB, а не en-US, потому что порядок «день месяц» принят и в
 * ОАЭ, и в Бахрейне, и совпадает с русским. Часовой пояс от языка не зависит.
 */
function when(iso: string): string {
  return new Date(iso).toLocaleString(currentLang() === "ru" ? "ru-RU" : "en-GB", {
    timeZone: "Asia/Dubai",
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}


